import type Phaser from 'phaser';
import type { TutorialDeps } from './types';
import {
  createTutorialArrow,
  updateTutorialArrow,
  getHintBottomCenterInViewport,
  startArrowFlow,
  createTutorialHintDom,
  positionTutorialHint,
  destroyTutorialHint,
  startTargetBounce,
  createBoardSpotlight,
  updateBoardSpotlight,
  STEP1_DIM_ALPHA,
} from './ui';

const HINT_TEXT = 'Перетащи кусочек на поле';
const TARGET_TOP_OFFSET = 8;

export type Step1State = {
  targetPieceId: number;
  arrow: Phaser.GameObjects.Container;
  hintEl: HTMLElement;
  stopBounce: (() => void) | null;
  stopArrowFlow: (() => void) | null;
  boardSpotlight: Phaser.GameObjects.Graphics | null;
};

export function getTargetPieceId(deps: TutorialDeps): number | null {
  const images = deps.getTrayImages();
  if (images.length === 0) return null;
  return images[0].getData('pieceId') as number;
}

export function getTargetBounds(
  deps: TutorialDeps,
  targetPieceId: number
): { centerX: number; topY: number; width: number; height: number } | null {
  const images = deps.getTrayImages();
  const img = images.find((i) => i.getData?.('pieceId') === targetPieceId);
  if (!img || !img.active) return null;
  const b = img.getBounds();
  return {
    centerX: b.centerX,
    topY: b.top,
    width: b.width,
    height: b.height,
  };
}

export function runStep1(deps: TutorialDeps): Step1State | null {
  const targetPieceId = getTargetPieceId(deps);
  if (targetPieceId == null) return null;

  const arrow = createTutorialArrow(deps.scene);
  const boardSpotlight = createBoardSpotlight(deps.scene);
  const hintEl = createTutorialHintDom(HINT_TEXT);

  const images = deps.getTrayImages();
  for (const img of images) {
    const pid = img.getData?.('pieceId') as number | undefined;
    if (pid == null) continue;
    if (pid === targetPieceId) {
      img.setAlpha(1);
    } else {
      img.setAlpha(STEP1_DIM_ALPHA);
    }
  }

  const targetImg = images.find((i) => i.getData?.('pieceId') === targetPieceId) ?? null;
  let stopBounce: (() => void) | null = null;
  if (targetImg) {
    stopBounce = startTargetBounce(deps.scene, targetImg);
  }

  let stopArrowFlow: (() => void) | null = null;

  const layout = (): void => {
    const bounds = getTargetBounds(deps, targetPieceId);
    const tr = deps.getTrayRect();
    const canvasRect = deps.scene.game.canvas.getBoundingClientRect();
    const cam = deps.scene.cameras.main;
    positionTutorialHint(hintEl, tr, canvasRect, cam.height);

    if (bounds) {
      const from = getHintBottomCenterInViewport(hintEl, canvasRect, cam);
      const to = {
        x: bounds.centerX,
        y: bounds.topY - TARGET_TOP_OFFSET,
      };
      updateTutorialArrow(arrow, from, to);
      arrow.setVisible(true);
      if (!stopArrowFlow) {
        stopArrowFlow = startArrowFlow(deps.scene, arrow);
      }
    } else {
      arrow.setVisible(false);
      if (stopArrowFlow) {
        stopArrowFlow();
        stopArrowFlow = null;
      }
    }
  };

  layout();
  deps.onTrayLayout?.();

  return {
    targetPieceId,
    arrow,
    hintEl,
    stopBounce,
    stopArrowFlow,
    boardSpotlight,
  };
}

export function step1Layout(state: Step1State, deps: TutorialDeps): void {
  const bounds = getTargetBounds(deps, state.targetPieceId);
  const tr = deps.getTrayRect();
  const canvasRect = deps.scene.game.canvas.getBoundingClientRect();
  const cam = deps.scene.cameras.main;
  positionTutorialHint(state.hintEl, tr, canvasRect, cam.height);

  if (bounds) {
    const from = getHintBottomCenterInViewport(state.hintEl, canvasRect, cam);
    const to = {
      x: bounds.centerX,
      y: bounds.topY - TARGET_TOP_OFFSET,
    };
    updateTutorialArrow(state.arrow, from, to);
    state.arrow.setVisible(true);
  } else {
    state.arrow.setVisible(false);
  }

  if (state.boardSpotlight?.visible) {
    const boardRect = deps.getBoardRect();
    const targetRect = deps.getBoardTargetRect(state.targetPieceId);
    if (boardRect) updateBoardSpotlight(state.boardSpotlight, boardRect, targetRect ?? null);
  }
}

export function step1OnDragStart(state: Step1State, deps: TutorialDeps): void {
  if (state.stopBounce) {
    state.stopBounce();
    state.stopBounce = null;
  }
  const boardRect = deps.getBoardRect();
  const targetRect = deps.getBoardTargetRect(state.targetPieceId);
  if (state.boardSpotlight && boardRect) {
    updateBoardSpotlight(state.boardSpotlight, boardRect, targetRect ?? null);
    state.boardSpotlight.setVisible(true);
  }
}

export function step1OnDragEnd(state: Step1State): void {
  if (state.boardSpotlight) {
    state.boardSpotlight.setVisible(false);
    state.boardSpotlight.clear();
  }
}

/** Перезапустить пульсацию целевого кусочка после возврата в лоток (drop вне targetRect). */
export function step1RestartBounce(
  state: Step1State,
  scene: Phaser.Scene,
  targetImg: Phaser.GameObjects.Image
): void {
  if ((targetImg.getData?.('pieceId') as number) !== state.targetPieceId) return;
  if (state.stopBounce) state.stopBounce();
  state.stopBounce = startTargetBounce(scene, targetImg);
}

export function destroyStep1(state: Step1State | null, deps: TutorialDeps): void {
  if (!state) return;
  if (state.stopBounce) state.stopBounce();
  if (state.stopArrowFlow) state.stopArrowFlow();
  if (state.boardSpotlight) state.boardSpotlight.destroy();
  state.arrow.destroy();
  destroyTutorialHint(state.hintEl);
  const images = deps.getTrayImages();
  for (const img of images) {
    img.setAlpha(1);
  }
}
